import React from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  ComposedChart,
} from "recharts";
import {
  Box,
  Typography,
  Paper,
  Grid,
  useTheme,
  Stack,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { People, Assignment, CheckCircle } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import MetricCard from "./MetricCard";
import ChartCard from "./ChartCard";

const chartColors = {
  accepted: "#50C878",
  partial: "#f6b580", // using the new partial color
  rejected: "#F44336",
  pending: "#2196F3",
  errored: "#9C27B0",
  active: "#50C878",
  notActive: "#F44336",
  primary: "#3F51B5",
  secondary: "#607D8B",
  cancelled: "#ff7fff",
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border border-gray-100 shadow-xl rounded-lg min-w-[120px]">
        <p className="text-[10px] font-bold text-gray-400 mb-1 uppercase border-b pb-1">
          {label}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 mt-1">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: entry.payload?.color || entry.color }}
            />
            <div className="flex gap-1 text-[11px] font-semibold text-gray-700">
              <span>{entry.name}:</span>
              <span className="font-bold">{entry.value}</span>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const Dashboard = ({ data }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const verificationData = [
    {
      name: t("Active"),
      value: data.active_verification,
      color: chartColors.accepted,
    },
    {
      name: t("Not Active"),
      value: data.not_active_verification,
      color: chartColors.rejected,
    },
  ];

  const comparisonData = [
    {
      name: t("Accepted"),
      claims: data.accepted_claims,
      preAuth: data.accepted_pre_auth,
    },
    {
      name: t("Partial"),
      claims: data.partial_claims,
      preAuth: data.partial_pre_auth,
    },
    {
      name: t("Rejected"),
      claims: data.rejected_claims,
      preAuth: data.rejected_pre_auth,
    },
    {
      name: t("Queued"),
      claims: data.pending_claims,
      preAuth: data.pending_pre_auth,
    },
    {
      name: t("Errored"),
      claims: data.errored_claims,
      preAuth: data.errored_pre_auth,
    },
  ];

  // Calculate percentages
  const claimsApprovalRate =
    ((data.accepted_claims / data.total_claims) * 100).toFixed(1) || 0;
  const preAuthApprovalRate =
    ((data.accepted_pre_auth / data.total_pre_auth) * 100).toFixed(1) || 0;
  const activeVerificationRate =
    ((data.active_verification / data.total_verification) * 100).toFixed(1) ||
    0;

  return (
    <Box>
      <Grid container spacing={1} sx={{ mb: 1 }}>
        <Grid item xs={12} md={3}>
          <MetricCard
            title={t("Total Patients")}
            value={data.patients}
            icon={<People fontSize="small" />}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            title={t("Total Eligibilities")}
            value={data.total_verification}
            icon={<CheckCircle fontSize="small" />}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            title={t("Total Claims")}
            value={data.total_claims}
            icon={<Assignment fontSize="small" />}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            title={t("Total Pre-Auth")}
            value={data.total_pre_auth}
            icon={<CheckCircle fontSize="small" />}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={12} md={9}>
          <ChartCard
            title={t(
              "Comparison of current status between Claims and Pre-Auth",
            )}
            subtitle={`${data.total_claims} ${t("total claims")} / ${
              data.total_pre_auth
            } ${t("total preauth requests")}`}
          >
            <Box sx={{ width: "100%", height: 250 }}>
              <ResponsiveContainer>
                <AreaChart
                  data={comparisonData}
                  margin={{ top: 10, right: 10, left: -25, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="colorClaims"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#0070C2" stopOpacity={0.2} />
                      <stop
                        offset="95%"
                        stopColor="#0070C2"
                        stopOpacity={0.01}
                      />
                    </linearGradient>

                    <linearGradient
                      id="colorPreAuth"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor={chartColors.accepted}
                        stopOpacity={0.3}
                      />
                      <stop
                        offset="95%"
                        stopColor={chartColors.accepted}
                        stopOpacity={0.01}
                      />
                    </linearGradient>
                  </defs>

                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f0f0f0"
                  />

                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: "#9CA3AF" }}
                  />

                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: "#9CA3AF" }}
                  />

                  <Tooltip content={<CustomTooltip />} />

                  <Legend
                    verticalAlign="top"
                    align="right"
                    iconType="circle"
                    wrapperStyle={{ fontSize: "10px", paddingBottom: "20px" }}
                  />

                  <Area
                    type="monotone"
                    dataKey="claims"
                    name={t("Claims")}
                    stroke="#0070C2"
                    strokeWidth={1.5}
                    fill="url(#colorClaims)"
                    activeDot={{ r: 4, strokeWidth: 0 }}
                  />

                  <Area
                    type="monotone"
                    dataKey="preAuth"
                    name={t("Pre-Authorization")}
                    stroke={chartColors.accepted}
                    strokeWidth={1.5}
                    fill="url(#colorPreAuth)"
                    activeDot={{ r: 4, strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={3}>
          <ChartCard
            title={t("Verification Status")}
            subtitle={`${data.total_verification} ${t("total verifications")}`}
            title2={t("Active Rate")}
            subtitle2={`${activeVerificationRate}%`}
          >
            <Box sx={{ width: "100%", height: 230, mt: 1 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={verificationData}
                    cx="50%"
                    cy="45%"
                    innerRadius={65}
                    outerRadius={85}
                    paddingAngle={8}
                    dataKey="value"
                    stroke="none"
                  >
                    {verificationData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.color}
                        style={{
                          filter: `drop-shadow(0px 4px 4px ${entry.color}33)`,
                        }}
                      />
                    ))}
                  </Pie>

                  <Tooltip content={<CustomTooltip />} />

                  <Legend
                    verticalAlign="bottom"
                    align="center"
                    iconType="circle"
                    iconSize={10}
                    wrapperStyle={{ fontSize: "11px", paddingTop: "10px" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard
            title={t("Pre-Authorization")}
            subtitle={`${data.total_pre_auth} ${t("total requests")}`}
            title2={t("Pre-Auth Approval Rate")}
            subtitle2={`${preAuthApprovalRate}%`}
          >
            <Box sx={{ width: "100%", height: 230, mt: 1 }}>
              <ResponsiveContainer>
                <BarChart
                  data={[
                    {
                      name: t("Accepted"),
                      value: data.accepted_pre_auth,
                      color: chartColors.accepted,
                    },
                    {
                      name: t("Partial"),
                      value: data.partial_pre_auth,
                      color: chartColors.partial,
                    },
                    {
                      name: t("Rejected"),
                      value: data.rejected_pre_auth,
                      color: chartColors.rejected,
                    },
                    {
                      name: t("Queued"),
                      value: data.pending_pre_auth,
                      color: chartColors.pending,
                    },
                    {
                      name: t("Errored"),
                      value: data.errored_pre_auth,
                      color: chartColors.errored,
                    },
                  ]}
                  margin={{ top: 10, right: 10, left: -25, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f5f5f5"
                  />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 9, fill: "#9CA3AF" }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 9, fill: "#9CA3AF" }}
                  />

                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "#f3f4f6", opacity: 0.4 }}
                  />

                  <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={22}>
                    <Cell fill={chartColors.accepted} />
                    <Cell fill={chartColors.partial} />
                    <Cell fill={chartColors.rejected} />
                    <Cell fill={chartColors.pending} />
                    <Cell fill={chartColors.errored} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard
            title={t("Claims Breakdown")}
            subtitle={`${data.total_claims} ${t("total claims")}`}
            title2={t("Claims Approval Rate")}
            subtitle2={`${claimsApprovalRate}%`}
          >
            <Box sx={{ width: "100%", height: 230, mt: 1 }}>
              <ResponsiveContainer>
                <BarChart
                  data={[
                    {
                      name: t("Accepted"),
                      value: data.accepted_claims,
                      color: chartColors.accepted,
                    },
                    {
                      name: t("Partial"),
                      value: data.partial_claims,
                      color: chartColors.partial,
                    },
                    {
                      name: t("Rejected"),
                      value: data.rejected_claims,
                      color: chartColors.rejected,
                    },
                    {
                      name: t("Queued"),
                      value: data.pending_claims,
                      color: chartColors.pending,
                    },
                    {
                      name: t("Cancelled"),
                      value: data.cancelled_claims,
                      color: chartColors.cancelled,
                    },
                    {
                      name: t("Errored"),
                      value: data.errored_claims,
                      color: chartColors.errored,
                    },
                  ]}
                  margin={{ top: 10, right: 10, left: -25, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f5f5f5"
                  />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 9, fill: "#9CA3AF" }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 9, fill: "#9CA3AF" }}
                  />

                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "#f3f4f6", opacity: 0.4 }}
                  />

                  <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={22}>
                    <Cell fill={chartColors.accepted} />
                    <Cell fill={chartColors.partial} />
                    <Cell fill={chartColors.rejected} />
                    <Cell fill={chartColors.pending} />
                    <Cell fill={chartColors.cancelled} />
                    <Cell fill={chartColors.errored} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};
export default Dashboard;
